import { logger } from '@disy/lib/logging';

import type { Modal } from 'ui/dialog/modal/modal';

import { getWorkbookDataProtectionContext } from 'cadenza/api-client/data-protection-context-api';
import { getWorkbookDeletionDeadline } from 'cadenza/api-client/deletion-deadline-api';
import type { DeletionDeadlineInfoDto } from 'cadenza/api-client/dto/deletion-deadline-dto';
import { isFeatureAvailable } from 'cadenza/features';
import type { DataProtectionContextId } from 'cadenza/repository/model/data-protection-context-id.gen';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { RepositoryTreeNodeDto } from 'cadenza/repository/model/repository-tree-node-dto';
import { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import { repositoryService } from 'cadenza/repository/repository-service';
import { getStore } from 'cadenza/store/cadenza-store';
import {
  getWorkbookNameAndHash,
  hasBoundObjecttypes,
  isWorkbookDeletionDeadlineEnabled,
  isWorkbookSaved,
} from 'cadenza/workbook/workbook-core-selectors.state';

export type SaveAsAction = (params: SaveAsActionParameters) => Promise<unknown>;

export interface SaveAsActionParameters {
  name: string;
  repositoryName: RepositoryName;
  pathToFolder?: string[];
  folderItem?: RepositoryTreeNodeDto;
  labels: string[];
  publish?: boolean;
  versionComment?: string;
  modal?: Modal<unknown>;
  projectId?: ProjectId;
  dataProtectionContextId?: DataProtectionContextId;
  deletionDeadlineInfo?: DeletionDeadlineInfoDto;
}

export interface SaveAsOptions {
  defaultName: string;
  defaultRepositoryName: RepositoryName;
  nameLabel: string;
  projectId?: ProjectId;
  presetDataProtectionContextId?: DataProtectionContextId;
  presetDeletionDeadlineInfo?: DeletionDeadlineInfoDto;
  labels: string[];
  afterSaveAction?: {
    label: string;
    execute: (returnValue: unknown) => Promise<void>;
  };
}

export async function saveAsWorkbook(save: SaveAsAction, options: SaveAsOptions) {
  const workbookId = WorkbookId.of(options.defaultRepositoryName, getWorkbookNameAndHash(getStore().getState()).id);
  try {
    let [{ SaveAsWizard }, writableDatabaseRepositories, dpcDto, deletionDeadlineInfo] = await Promise.all([
      import('cadenza/workbook/save-as-wizard/save-as-wizard'),
      repositoryService().listWritable(),
      isFeatureAvailable('DATA_PROTECTION') ? getWorkbookDataProtectionContext(workbookId) : undefined,
      (
        isWorkbookSaved(getStore().getState()) &&
        isFeatureAvailable('DELETION_DEADLINE') &&
        isWorkbookDeletionDeadlineEnabled(getStore().getState())
      ) ?
        getWorkbookDeletionDeadline(workbookId)
      : undefined,
    ]);
    options.presetDataProtectionContextId = dpcDto ? dpcDto.itemId : undefined;
    options.presetDeletionDeadlineInfo = deletionDeadlineInfo;
    if (isWorkbookSaved(getStore().getState()) && hasBoundObjecttypes(getStore().getState())) {
      // already saved workbooks will duplicate bound objecttypes and data on saveAs,
      // thus the target repository needs a cmdb
      writableDatabaseRepositories = writableDatabaseRepositories.filter((repository) => repository.cadenzaManagedDB);
    }
    const dialog = new SaveAsWizard(writableDatabaseRepositories, save, options);
    return show(dialog);
  } catch (error) {
    logger.error('Failed to show save-as dialog.', error);
  }
}

function show(dialog: Modal<unknown>) {
  return new Promise((resolve, reject) => {
    dialog.addEventListener('cancel', () => resolve(false));
    dialog.addEventListener('close', () => {
      dialog.remove();
      if (dialog.returnValue) {
        resolve(dialog.returnValue);
      } else {
        resolve(true);
      }
    });
    document.body.appendChild(dialog);
    return dialog.showModal();
  });
}
